import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, SpinLoading } from 'antd-mobile';
import zhCN from 'antd-mobile/es/locales/zh-CN';
import useAuthStore from './store/authStore';
import PageViewTracker from './components/PageViewTracker';
import LanguageSwitcher from './components/LanguageSwitcher';
import Offline from './pages/Offline';
import NotFound from './pages/NotFound';

// 代码拆分 - 懒加载页面组件
const Home = lazy(() => import('./pages/Home'));
const Plans = lazy(() => import('./pages/Plans'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileEdit = lazy(() => import('./pages/ProfileEdit'));
const LoginForm = lazy(() => import('./components/LoginForm'));
const RegisterForm = lazy(() => import('./components/RegisterForm'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PlanDetail = lazy(() => import('./pages/PlanDetail'));
const PlanCreate = lazy(() => import('./pages/PlanCreate'));
const PlanEdit = lazy(() => import('./pages/PlanEdit'));
const PostsDetail = lazy(() => import('./pages/PostsDetail'));
const Matches = lazy(() => import('./pages/Matches'));

// 认证保护组件
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// 公共路由组件
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <SpinLoading />
  </div>
);

function AppContent() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <>
      <PageViewTracker />
      <div className={`language-switcher-container ${isHomePage ? 'on-hero' : ''}`}>
        <LanguageSwitcher />
      </div>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* 公共路由 */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginForm />
            </PublicRoute>
          } 
        />
        <Route
          path="/offline"
          element={<Offline />}
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <RegisterForm />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } 
        />

        {/* 首页改为公共路由，不再强制登录 */}
        <Route 
          path="/" 
          element={<Home />} 
        />
        <Route 
          path="/plans" 
          element={
            <ProtectedRoute>
              <Plans />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plans/my" 
          element={
            <ProtectedRoute>
              <Plans />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plans/create" 
          element={
            <ProtectedRoute>
              <PlanCreate />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plans/:id" 
          element={
            <ProtectedRoute>
              <PlanDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plans/:id/edit" 
          element={
            <ProtectedRoute>
              <PlanEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/posts/detail" 
          element={
            <ProtectedRoute>
              <PostsDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/matches" 
          element={
            <ProtectedRoute>
              <Matches />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/profile/edit" 
          element={
            <ProtectedRoute>
              <ProfileEdit />
            </ProtectedRoute>
          } 
        />

        {/* 默认 404 页面 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <Router>
        <AppContent />
      </Router>
    </ConfigProvider>
  );
}

export default App;
